import {Directive, ElementRef, HostListener} from '@angular/core';

/**
 * De-wraps text pasted into prose textareas. Content copied from PDFs, Word, or
 * LinkedIn carries a hard line break at every visual line, so it lands in the box
 * "split across lines". This collapses those soft wraps back into flowing
 * paragraphs while preserving intentional paragraph breaks (blank lines).
 *
 * Auto-applies to every `textarea[nbInput]`. Opt out with the `rawpaste`
 * attribute on fields where line breaks are meaningful (e.g. one item per line).
 * Typed input is never touched - only the paste event is normalised.
 */
@Directive({
  selector: 'textarea[nbInput]',
  standalone: false
})
export class SmartPasteDirective {

  constructor(private host: ElementRef<HTMLTextAreaElement>) {}

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    const el = this.host.nativeElement;
    if (el.hasAttribute('rawpaste')) {
      return;
    }

    const pasted = event.clipboardData?.getData('text/plain');
    if (!pasted || !/[\r\n]/.test(pasted)) {
      return;
    }

    const cleaned = dewrapPastedText(pasted);
    if (cleaned === pasted) {
      return;
    }

    event.preventDefault();
    el.focus();

    // execCommand keeps the native undo stack and fires `input` so the
    // reactive-form CVA picks the new value up. It is deprecated and can throw
    // or return false in some browsers, hence the manual-splice fallback below.
    let inserted = false;
    try {
      inserted = document.execCommand('insertText', false, cleaned);
    } catch {
      inserted = false;
    }
    if (inserted) {
      return;
    }

    // Native paste is already cancelled, so the fallback must write something.
    // Only trust the caret while the element actually holds focus.
    try {
      const focused = document.activeElement === el;
      const start = focused ? (el.selectionStart ?? el.value.length) : el.value.length;
      const end = focused ? (el.selectionEnd ?? el.value.length) : el.value.length;
      el.value = el.value.slice(0, start) + cleaned + el.value.slice(end);
      const caret = start + cleaned.length;
      el.setSelectionRange(caret, caret);
      el.dispatchEvent(new Event('input', {bubbles: true}));
    } catch (err) {
      console.error('[SmartPasteDirective] paste insert failed', err);
    }
  }

}

/**
 * Collapses soft line wraps into spaces, keeping blank-line paragraph breaks.
 * Pure and exported to keep the de-wrap logic unit-testable in isolation.
 */
export function dewrapPastedText(text: string): string {
  return text
    .replace(/\r\n?/g, '\n')          // normalise newlines
    .split(/\n[ \t]*\n+/)             // split on blank-line paragraph breaks
    .map(paragraph => paragraph.replace(/[ \t]*\n[ \t]*/g, ' ').trim())
    .filter(paragraph => paragraph.length > 0)
    .join('\n\n');
}
